import React from 'react';
import { useJobStream } from '../../hooks/useJobStream';
import { useLanguage } from '../../hooks/useLanguage';
import jsonAnimation from "../../assets/thinking-animation.gif";
import { useJobStreamUiPublisher, useJobStreamUiReader } from './JobStreamUiContext';
import { getStreamFailureMessage } from '../../utils/processingErrorUtils';

function formatMarkdownText(content) {
  if (!content) return content;

  const processBold = (text) => {
    const parts = [];
    let lastIndex = 0;
    const boldRegex = /\*\*(.+?)\*\*/g;
    let match;

    while ((match = boldRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      parts.push(<strong key={match.index} className="!font-semibold">{match[1]}</strong>);
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  return content.split('\n').map((line, index) => {
    const headerMatch = line.match(/^(#{1,3})\s+(.+)$/);
    if (headerMatch) {
      const level = headerMatch[1].length;
      const lineText = headerMatch[2];
      const className = level === 1
        ? '!text-base !font-bold !mt-4 !mb-2 !text-gray-900'
        : level === 2
          ? '!text-sm !font-semibold !mt-3 !mb-2 !text-gray-800'
          : '!text-xs !font-semibold !mt-2 !mb-1 !text-gray-700';

      return (
        <div key={index} className={className}>
          {processBold(lineText)}
        </div>
      );
    }
    return (
      <div key={index} className="text-xs">
        {processBold(line || '\u00A0')}
      </div>
    );
  });
}

export function JobStreamDisplayView({
  thinking,
  text,
  isThinking,
  sectionKey,
  isSpanish,
}) {
  const [fadeKey, setFadeKey] = React.useState(0);
  const thinkingContentRef = React.useRef(null);
  const textContentRef = React.useRef(null);

  React.useEffect(() => {
    if (sectionKey > 0) {
      setFadeKey((prev) => prev + 1);
    }
  }, [sectionKey]);

  React.useEffect(() => {
    if (thinkingContentRef.current && thinking) {
      thinkingContentRef.current.scrollTop = thinkingContentRef.current.scrollHeight;
    }
  }, [thinking]);

  React.useEffect(() => {
    if (textContentRef.current && text) {
      textContentRef.current.scrollTop = textContentRef.current.scrollHeight;
    }
  }, [text]);

  const hasContent = isThinking || (text && text.trim().length > 0);
  if (!hasContent) {
    return null;
  }

  return (
    <div className="mt-4 space-y-3 h-[280px] overflow-y-hidden" style={{ minHeight: '280px' }}>
      {isThinking && (
        <div className="thinking-section shine">
          <div className="flex items-center gap-2 mb-3 p-1">
            <img
              src={jsonAnimation}
              alt="Thinking"
              className="w-4 h-4"
              style={{ display: 'block' }}
            />
            <div className="thinking-section-header text-xs font-semibold !text-default !font-normal">
              <span className="inline-block animate-pulse">{isSpanish ? 'Pensando...' : 'Thinking...'}</span>
            </div>
          </div>
          {thinking && thinking.trim().length > 0 ? (
            <div
              ref={thinkingContentRef}
              key={fadeKey}
              className="text-xs leading-relaxed fade-in-section max-h-[300px] overflow-y-auto scrollbar-hide"
              style={{ minHeight: '20px' }}
            >
              {formatMarkdownText(thinking)}
            </div>
          ) : (
            <div className="text-xs text-gray-500 italic">
              <span className="inline-block animate-pulse">{isSpanish ? 'Analizando documentos...' : 'Analyzing documents...'}</span>
            </div>
          )}
        </div>
      )}

      {text && text.trim().length > 0 && (
        <div className="">
          <div className="flex items-center gap-2 mb-2">
            <img
              src={jsonAnimation}
              alt="Processing"
              className="w-4 h-4"
              style={{ display: 'block' }}
            />
            <div className="thinking-section-header text-xs font-semibold !text-default !font-normal">
              {isSpanish ? 'Procesando...' : 'Processing...'}
            </div>
          </div>
          <div className="relative">
            <div
              ref={textContentRef}
              className="text-xs text-gray-800 whitespace-pre-wrap leading-relaxed prose prose-sm max-w-none font-mono text-xs max-h-[300px] overflow-y-auto scrollbar-hide"
              style={{ minHeight: '20px', wordBreak: 'break-word' }}
            >
              {text}
            </div>
            <div className="absolute top-0 left-0 right-0 h-10 bg-gradient-to-b from-white to-transparent pointer-events-none" style={{ zIndex: 1 }}></div>
            <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent pointer-events-none" style={{ zIndex: 1 }}></div>
          </div>
        </div>
      )}
    </div>
  );
}

function JobStreamDisplayConnected({ jobId, onStatusUpdate, hideUi, publishUi, isSpanish }) {
  const { status, thinking, text, isThinking, sectionKey } = useJobStream(jobId);
  const setJobStreamUi = useJobStreamUiPublisher();

  const lastStatusRef = React.useRef(null);
  const updateInProgressRef = React.useRef(false);
  const prevIsThinkingRef = React.useRef(isThinking);

  const onStatusUpdateRef = React.useRef(onStatusUpdate);
  const onStatusUpdateRefForStatus = React.useRef(onStatusUpdate);

  React.useEffect(() => {
    onStatusUpdateRef.current = onStatusUpdate;
    onStatusUpdateRefForStatus.current = onStatusUpdate;
  }, [onStatusUpdate]);

  React.useEffect(() => {
    if (publishUi && setJobStreamUi && jobId) {
      setJobStreamUi(jobId, { thinking, text, isThinking, sectionKey });
    }
  }, [jobId, publishUi, setJobStreamUi, thinking, text, isThinking, sectionKey]);

  React.useEffect(() => {
    if (prevIsThinkingRef.current !== isThinking && onStatusUpdateRef.current) {
      prevIsThinkingRef.current = isThinking;
      onStatusUpdateRef.current({ isThinking });
    }
  }, [isThinking]);

  React.useEffect(() => {
    if (!status || !onStatusUpdateRefForStatus.current) return;

    if (status.stage === 'stream_closed') {
      const streamFailureOnClose = getStreamFailureMessage(status);
      if (streamFailureOnClose) {
        onStatusUpdateRefForStatus.current({
          processingStatus: 'error',
          error: streamFailureOnClose,
          jobId: status.id || jobId,
        });
        lastStatusRef.current = `stream_closed-error-${status.id || jobId}`;
        updateInProgressRef.current = false;
        return;
      }

      const updateData = {
        areStepsCompleted: true,
        currentStep: 2,
        completedSteps: [0, 1, 2],
        processingStatus: 'completed',
        jobId: status.id || jobId,
        ocrInputId: status.ocrInputId
      };
      onStatusUpdateRefForStatus.current(updateData);
      lastStatusRef.current = `stream_closed-${status.id || jobId}`;
      updateInProgressRef.current = false;
      return;
    }

    if (updateInProgressRef.current) {
      return;
    }

    const statusKey = JSON.stringify({
      id: status.id || jobId,
      status: status.status,
      stage: status.stage,
      ocrInputId: status.ocrInputId,
      hasResult: !!status.result
    });

    if (lastStatusRef.current === statusKey) {
      return;
    }
    lastStatusRef.current = statusKey;
    updateInProgressRef.current = true;

    if (status.status === 'completed' && status.result) {
      onStatusUpdateRefForStatus.current({
        areStepsCompleted: true,
        currentStep: 2,
        completedSteps: [0, 1, 2],
        processingStatus: 'completed',
        jobId: status.id || jobId,
        ocrInputId: status.ocrInputId
      });
      updateInProgressRef.current = false;
      return;
    }

    const streamFailureMessage = getStreamFailureMessage(status);
    if (streamFailureMessage) {
      if (process.env.NODE_ENV === 'development') {
        console.error(`❌ [JobStreamDisplay] Stream error for job ${jobId}:`, {
          status: status.status,
          stage: status.stage,
          message: status.message,
          error: status.error,
          detail: status.detail,
          code: status.code,
          fullStatus: status,
        });
      }

      onStatusUpdateRefForStatus.current({
        processingStatus: 'error',
        error: streamFailureMessage,
        jobId: status.id || jobId
      });
    } else {
      onStatusUpdateRefForStatus.current({
        processingStatus: status.status || 'processing',
        jobId: status.id || jobId,
        isThinking: isThinking
      });
    }

    setTimeout(() => {
      updateInProgressRef.current = false;
    }, 0);
  }, [status, jobId, isThinking]);

  if (!jobId) {
    return null;
  }
  if (hideUi) {
    return null;
  }

  return (
    <JobStreamDisplayView
      thinking={thinking}
      text={text}
      isThinking={isThinking}
      sectionKey={sectionKey}
      isSpanish={isSpanish}
    />
  );
}

function JobStreamDisplayViewOnly({ jobId, isSpanish }) {
  const ui = useJobStreamUiReader(jobId);
  if (!jobId) return null;
  if (!ui) {
    return (
      <div className="mt-4 text-xs text-gray-500 italic">
        {isSpanish ? 'Conectando al stream...' : 'Connecting to stream...'}
      </div>
    );
  }
  return (
    <JobStreamDisplayView
      thinking={ui.thinking ?? ''}
      text={ui.text ?? ''}
      isThinking={!!ui.isThinking}
      sectionKey={ui.sectionKey ?? 0}
      isSpanish={isSpanish}
    />
  );
}

/**
 * @param {string} jobId
 * @param {function} onStatusUpdate
 * @param {boolean} [hideUi] — run stream + callbacks only (no visible panel)
 * @param {boolean} [publishUi] — mirror thinking/text into JobStreamUiContext for viewOnly consumers
 * @param {boolean} [viewOnly] — read from context only (no second SSE); use with publishUi on hidden instance
 */
export const JobStreamDisplay = ({
  jobId,
  onStatusUpdate,
  hideUi = false,
  publishUi = false,
  viewOnly = false,
}) => {
  const [isSpanish] = useLanguage();

  if (viewOnly) {
    return <JobStreamDisplayViewOnly jobId={jobId} isSpanish={isSpanish} />;
  }

  return (
    <JobStreamDisplayConnected
      jobId={jobId}
      onStatusUpdate={onStatusUpdate}
      hideUi={hideUi}
      publishUi={publishUi}
      isSpanish={isSpanish}
    />
  );
};
